import type { CSSProperties } from "react";
import type { ConfiguratorOption } from "../../features/vault/configurator";
import { isDmatOption } from "../../features/vault/applyOption";
import { sphereFill } from "../editor/library/sphereArt";
import type { SlotBarChoice } from "./SlotBar";

const intOf = (hex: string): number | undefined => {
  const h = hex.trim().replace("#", "");
  return /^[0-9a-f]{6}$/i.test(h) ? parseInt(h, 16) : undefined;
};

/**
 * How a configurator option's swatch is painted. Returns exactly ONE background
 * property — never the `background` shorthand beside a longhand (see
 * MaterialCategoryGrid's `swatchStyle` for the React bug that causes).
 *
 * - A rendered thumbnail when the library had one (what tells a brushed gold
 *   from a polished one).
 * - A drawn sphere for a library material without one: a lit metal ball, a
 *   faceted stone, a glazed ceramic, a pearl.
 * - A LEGACY option (colour only, authored before the library) keeps the flat
 *   colour chip it always had, so an old shared link looks exactly as it did.
 */
export function optionFill(option: ConfiguratorOption): CSSProperties {
  if (option.thumbUrl) return { backgroundImage: `url(${JSON.stringify(option.thumbUrl)})` };
  const p = option.preset;
  if (p && (p.url || p.presetId)) {
    return {
      backgroundImage: sphereFill({
        id: p.presetId ?? "",
        category: p.kind,
        color: intOf(option.swatch),
        format: p.url ? (isDmatOption(option) ? "dmat" : "pmat") : undefined,
      }),
    };
  }
  return { backgroundColor: option.swatch };
}

/** A part's options as the bar's swatches, the one at `activeIndex` ringed. */
export function choicesOf(
  options: ConfiguratorOption[],
  activeIndex: number,
  busyId?: string | null,
): SlotBarChoice[] {
  return options.map((o, i) => ({
    key: o.id,
    name: o.name,
    fill: optionFill(o),
    active: i === activeIndex,
    ...(busyId === o.id ? { busy: true } : {}),
  }));
}
